export interface PollOptions {
  initialMs?: number
  maxMs?: number
  /** Stop polling after this long. */
  limitMs?: number
  /** Called when polling ends by itself (done or time limit), not when stop() is called. */
  onFinish?: () => void
}

/**
 * Calls `tick` repeatedly, with the delay growing from 3 s to 15 s, until `tick` resolves
 * true or the time limit (10 minutes) passes. Errors are swallowed: the next tick retries.
 * Returns a stop function.
 */
export function pollWithBackoff(tick: () => Promise<boolean>, options: PollOptions = {}): () => void {
  const initial = options.initialMs ?? 3000
  const max = options.maxMs ?? 15000
  const limit = options.limitMs ?? 10 * 60 * 1000
  const startedAt = Date.now()
  let delay = initial
  let timer: ReturnType<typeof setTimeout> | null = null
  let stopped = false

  const schedule = (): void => {
    timer = setTimeout(() => {
      void run()
    }, delay)
  }

  const run = async (): Promise<void> => {
    if (stopped) return
    let done = false
    try {
      done = await tick()
    } catch {
      done = false
    }
    if (stopped) return
    if (done || Date.now() - startedAt >= limit) {
      options.onFinish?.()
      return
    }
    delay = Math.min(max, Math.round(delay * 1.5))
    schedule()
  }

  schedule()

  return (): void => {
    stopped = true
    if (timer !== null) clearTimeout(timer)
  }
}
